import { useState, useEffect } from 'react'
import { apiClient } from '@/services/api/client'
import { Check, X, AlertTriangle, Loader2, UserCheck } from 'lucide-react'

interface LessonInfo {
  id: string
  time_slot_name: string
  subject_name: string
  klass_name: string
  date: string
  chain_id: number
}

interface TeacherInfo {
  id: string
  code: string
  name: string
  provisional_lesson_ids: string[]
}

interface CombinationAssignment {
  teacher_id: string
  teacher_code: string
  lesson_ids: string[]
}

interface Combination {
  id: number
  score: number
  covers_all: boolean
  assignments: CombinationAssignment[]
  broken_chains: number
  extra_arrivals: number
  is_optimum: boolean
}

interface WaitingPoolAssemblyData {
  lessons: LessonInfo[]
  teachers: TeacherInfo[]
  combinations: Combination[]
  has_waiting_pool: boolean
}

interface WaitingPoolMatrixProps {
  requestId: string
  hideWhenEmpty?: boolean
}

export function WaitingPoolMatrix({ requestId, hideWhenEmpty = false }: WaitingPoolMatrixProps) {
  const [data, setData] = useState<WaitingPoolAssemblyData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedCombo, setSelectedCombo] = useState<number | null>(null)
  const [assigning, setAssigning] = useState(false)
  const [assignResult, setAssignResult] = useState<string | null>(null)
  const [pendingWarning, setPendingWarning] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    apiClient.get(`/api/v1/admin/substitutions/${requestId}/waiting-pool`)
      .then(res => {
        setData(res.data)
        setSelectedCombo(res.data.combinations.length > 0 ? 0 : null)
        setError(null)
      })
      .catch(err => setError(err?.response?.data?.detail || 'Daten konnten nicht geladen werden'))
      .finally(() => setLoading(false))
  }, [requestId])

  const handleAssign = async (confirmSplit = false) => {
    if (!data || selectedCombo === null) return
    const combo = data.combinations[selectedCombo]
    if (!combo) return

    setAssigning(true)
    setAssignResult(null)
    try {
      const res = await apiClient.post(
        `/api/v1/admin/substitutions/${requestId}/waiting-pool/assign`,
        { assignments: combo.assignments, confirm_split: confirmSplit },
      )
      if (res.data?.needs_confirmation) {
        setPendingWarning(res.data.warning || 'Kette würde getrennt. Trotzdem zuweisen?')
        return
      }
      if (!res.data?.success) {
        setAssignResult(res.data?.warning || 'Zuweisung fehlgeschlagen')
        return
      }
      const warning: string | null = res.data?.warning || null
      setPendingWarning(null)
      setAssignResult(
        `Erfolgreich: ${res.data.assigned_count} Lektionen zugewiesen`
        + (warning ? ` — ⚠ ${warning}` : '')
      )
      setData(prev => prev ? { ...prev, combinations: [] } : null)
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setAssignResult(detail || 'Zuweisung fehlgeschlagen')
    } finally {
      setAssigning(false)
    }
  }

  if (loading) {
    if (hideWhenEmpty) return null
    return (
      <div className="p-4 flex items-center justify-center gap-2 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        Lade Waiting-Pool-Daten...
      </div>
    )
  }

  if (error) {
    return <div className="p-4 text-sm text-red-600">{error}</div>
  }

  if (!data || !data.has_waiting_pool) {
    if (hideWhenEmpty) return null
    return (
      <div className="p-4 text-sm text-gray-500 text-center">
        Kein aktiver Waiting-Pool für diese Anfrage.
      </div>
    )
  }

  const { lessons, teachers, combinations } = data

  if (lessons.length === 0) {
    return (
      <div className="p-4 text-sm text-green-600 flex items-center gap-2">
        <Check className="w-4 h-4" />
        Alle Lektionen sind zugewiesen.
      </div>
    )
  }

  const selectedCombination = selectedCombo !== null ? combinations[selectedCombo] : null

  // Build assignment lookup: lesson_id → teacher_code (from selected combination)
  const assignmentMap = new Map<string, string>()
  if (selectedCombination) {
    for (const a of selectedCombination.assignments) {
      for (const lid of a.lesson_ids) {
        assignmentMap.set(lid, a.teacher_code)
      }
    }
  }

  const body = (
    <div className={hideWhenEmpty ? 'px-5 py-4 border-b border-gray-200/30 space-y-4' : 'space-y-4'}>
      <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
        <AlertTriangle className="w-4 h-4 text-amber-500" />
        Waiting-Pool-Zusammenstellung
        <span className="text-xs text-gray-500 ml-auto">
          {teachers.length} Lehrer im Pool · {lessons.length} offene Lektionen
        </span>
      </div>

      {/* Matrix Table */}
      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                Zeit
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                Fach
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                Klasse
              </th>
              {teachers.map(t => (
                <th
                  key={t.id}
                  className="px-2 py-2 text-center font-semibold border-b border-l border-gray-200 dark:border-gray-700"
                >
                  <div className={`inline-flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                    selectedCombination?.assignments.some(a => a.teacher_id === t.id)
                      ? 'bg-blue-100 border-blue-500 text-blue-700 dark:bg-blue-900/30 dark:border-blue-400 dark:text-blue-400'
                      : 'bg-gray-100 border-gray-300 text-gray-600 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-400'
                  }`}>
                    {t.code}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lessons.map(lesson => (
              <tr
                key={lesson.id}
                className={`border-b border-gray-100 dark:border-gray-700 last:border-0 ${
                  lessons.filter(l => (l.chain_id ?? 0) === (lesson.chain_id ?? 0)).length > 1
                    ? 'border-l-4 border-l-indigo-400 dark:border-l-indigo-500'
                    : ''
                }`}
              >
                <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  {lesson.time_slot_name || '—'}
                </td>
                <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                  {lesson.subject_name || '—'}
                </td>
                <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  {lesson.klass_name || '—'}
                </td>
                {teachers.map(t => {
                  const canCover = t.provisional_lesson_ids.includes(lesson.id)
                  const isAssigned = assignmentMap.get(lesson.id) === t.code
                  return (
                    <td
                      key={t.id}
                      className={`px-2 py-2 text-center border-l border-gray-100 dark:border-gray-700 ${
                        isAssigned
                          ? 'bg-blue-100 dark:bg-blue-900/30'
                          : canCover
                            ? 'bg-green-50 dark:bg-green-900/20'
                            : 'bg-red-50 dark:bg-red-900/20'
                      }`}
                    >
                      {isAssigned ? (
                        <UserCheck className="w-4 h-4 text-blue-600 dark:text-blue-400 mx-auto" />
                      ) : canCover ? (
                        <Check className="w-3.5 h-3.5 text-green-600 dark:text-green-400 mx-auto" />
                      ) : (
                        <X className="w-3.5 h-3.5 text-red-400 dark:text-red-500 mx-auto" />
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Combination selector */}
      {combinations.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Kombinationsvorschläge ({combinations.length}):
          </div>
          <div className="flex gap-2 flex-wrap">
            {combinations.map((combo, idx) => (
              <button
                key={idx}
                onClick={() => { setPendingWarning(null); setSelectedCombo(idx) }}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                  selectedCombo === idx
                    ? 'bg-blue-500 text-white border-blue-500'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                }`}
              >
                  Vorschlag {idx + 1}: {combo.assignments.map(a => a.teacher_code).join(' + ')}
                  <span className="ml-1 opacity-70">({Math.round(combo.score * 100)}%)</span>
                  {combo.broken_chains > 0 && (
                    <span className="ml-1 text-amber-600 dark:text-amber-400">Kette getrennt</span>
                  )}
              </button>
            ))}
          </div>

          {/* Selected combination detail */}
          {selectedCombination && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
              <div className="text-xs font-medium text-blue-800 dark:text-blue-300 mb-2">
                Vorschlag {selectedCombo! + 1} — Zuweisung:
              </div>
              <div className="space-y-1">
                {selectedCombination.assignments.map((a, i) => (
                  <div key={i} className="text-xs text-blue-700 dark:text-blue-400 flex items-center gap-2">
                    <span className="font-mono font-semibold bg-blue-100 dark:bg-blue-900/40 px-1.5 py-0.5 rounded">
                      {a.teacher_code}
                    </span>
                    → {a.lesson_ids.length} Lektion(en)
                  </div>
                ))}
              </div>

              {pendingWarning && (
                <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-300 dark:border-amber-700 space-y-2">
                  <div className="text-xs text-amber-900 dark:text-amber-200">{pendingWarning}</div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleAssign(true)}
                      disabled={assigning}
                      className="flex-1 px-3 py-1.5 bg-amber-500 text-white text-xs rounded-lg hover:bg-amber-600 disabled:opacity-50"
                    >
                      Trotzdem zuweisen
                    </button>
                    <button
                      onClick={() => setPendingWarning(null)}
                      disabled={assigning}
                      className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600"
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              )}

              <button
                onClick={() => handleAssign(false)}
                disabled={assigning || !!pendingWarning}
                className="mt-3 w-full px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 disabled:opacity-50 font-medium flex items-center justify-center gap-2"
              >
                {assigning ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Zuweisen...</>
                ) : (
                  <><UserCheck className="w-4 h-4" /> Diese Kombination zuweisen</>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {combinations.length === 0 && (
        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-700">
          <div className="text-sm text-amber-800 dark:text-amber-300">
            Keine vollständige Kombination aus dem Waiting-Pool möglich. Die Lektionen können nicht vollständig abgedeckt werden.
          </div>
        </div>
      )}

      {assignResult && (
        <div className={`p-3 rounded-lg text-sm ${
          assignResult.startsWith('Erfolgreich')
            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-700'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-700'
        }`}>
          {assignResult}
        </div>
      )}
    </div>
  )
  return body
}
